import React, { useEffect, useRef } from "react";

const FOCUSABLE = "button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex='-1'])";

// ENG-2482: a confirmation opens over the page. Escape, Cancel, × and the backdrop close it unless it is busy.
// `returnFocus` names where focus goes on close when the element that opened it is gone or hidden.
export default function CpDialog({ label, title, busy = false, onClose, returnFocus, children }) {
  const panel = useRef(null);
  const latest = useRef({ busy, onClose });
  latest.current = { busy, onClose };
  useEffect(() => {
    const opener = document.activeElement;
    panel.current?.focus();
    // A confirm button that disables itself drops focus to the page, so keys and focus outside the panel come back to it.
    const onFocusIn = (event) => { if (panel.current && !panel.current.contains(event.target)) panel.current.focus(); };
    const onKeyDown = (event) => {
      if (!panel.current || panel.current.contains(event.target)) return;
      if (event.key === "Escape") {
        event.stopPropagation();
        if (!latest.current.busy) latest.current.onClose();
      } else if (event.key === "Tab") {
        event.preventDefault();
        panel.current.focus();
      }
    };
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("keydown", onKeyDown, true);
      // An opener that unmounted with the click leaves the body as the active element.
      if (opener && opener !== document.body && opener.isConnected) opener.focus();
      if (document.activeElement !== opener || opener === document.body) returnFocus?.()?.focus();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- runs once per opening; returnFocus is read on close
  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      if (!busy) onClose();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = [...panel.current.querySelectorAll(FOCUSABLE)];
    if (!focusable.length) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  return (
    <div data-testid="cp-dialog-backdrop" className="fixed inset-0 z-50 flex items-start sm:items-center justify-center overflow-y-auto bg-black/40 p-4"
      onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }}>
      <div ref={panel} role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} onKeyDown={onKeyDown}
        className="w-full max-w-lg bg-white rounded-lg shadow-xl p-5 space-y-4 focus:outline-none">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button type="button" aria-label="Close" disabled={busy} className="px-1 text-xl leading-none text-gray-500 disabled:opacity-50" onClick={onClose}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}
